import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { i18n } from "../../translate/i18n";
import {
  usePanelStyles,
  ModalTitle,
  FormField,
  Callout,
} from "../QueueIntegrationModal/panelKit";

const useStyles = makeStyles(() => ({
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: { cursor: "pointer" },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const FlowBuilderInputModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const isMounted = useRef(true);
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [variableName, setVariableName] = useState("");
  const questionInputRef = useRef(null);

  const [labels, setLabels] = useState({
    title: "Adicionar input ao fluxo",
    btn: "Adicionar"
  });

  const exampleVariables = [
    { name: "nome", description: "Nome do cliente" },
    { name: "email", description: "Email do cliente" },
    { name: "telefone", description: "Telefone do cliente" },
    { name: "cidade", description: "Cidade do cliente" }
  ];

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar input do fluxo",
        btn: "Salvar alterações"
      });
      setQuestion(data.data.question || "");
      setVariableName(data.data.variableName || "");
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar input ao fluxo",
        btn: "Adicionar"
      });
      setQuestion("");
      setVariableName("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
    setLoading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      if (open === "edit") {
        handleClose();
        onUpdate({
          ...data,
          data: { 
            question: question,
            variableName: variableName
          }
        });
      } else if (open === "create") {
        handleClose();
        onSave({
          question: question,
          variableName: variableName
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleVariableClick = (variable) => {
   
    
    if (questionInputRef.current) {
      const inputElement = questionInputRef.current.querySelector('textarea');
      if (inputElement) {
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        const variableText = `\${${variable.name}}`;
        const newText = question.substring(0, start) + variableText + question.substring(end);
        setQuestion(newText);
        
       
        const newCursorPosition = start + variableText.length;
    
   
        setTimeout(() => {
          inputElement.focus();
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 100);
      }
    }
  };

  const isFormValid = () => {
    return question.trim() !== "" && variableName.trim() !== "";
  };

  return (
    <Dialog
      open={activeModal}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <ModalTitle
        title={labels.title}
        subtitle="Faz uma pergunta, espera a resposta e guarda numa variável."
        onClose={handleClose}
      />

      <DialogContent>
        <div className={panel.stack}>
          <FormField
            label="Pergunta"
            required
            help="A pergunta enviada ao usuário. Você pode incluir variáveis usando ${nomeVariavel}."
          >
            <TextField
              multiline
              minRows={4}
              name="question"
              variant="outlined"
              size="small"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              fullWidth
              ref={questionInputRef}
              placeholder="Ex: Olá ${nome}, qual é a sua idade?"
            />
          </FormField>

          <FormField label="Variáveis disponíveis (clique para inserir)">
            <div className={classes.chips}>
              {exampleVariables.map((variable) => (
                <Chip
                  key={variable.name}
                  label={variable.name}
                  title={variable.description}
                  className={classes.chip}
                  color="primary"
                  onClick={() => handleVariableClick(variable)}
                  size="small"
                />
              ))}
            </div>
          </FormField>

          <FormField
            label="Nome da variável"
            required
            help="A resposta do usuário será armazenada nesta variável. Não use espaços ou caracteres especiais."
          >
            <TextField
              name="variableName"
              variant="outlined"
              size="small"
              value={variableName}
              onChange={e => setVariableName(e.target.value)}
              fullWidth
              placeholder="Ex: idade_cliente"
            />
          </FormField>

          <Callout icon={<InfoOutlinedIcon />}>
            <strong>Como funciona.</strong> Este nó enviará a pergunta ao usuário e aguardará uma resposta.
            Quando o usuário responder, o valor será armazenado na variável especificada
            e o fluxo continuará para o próximo nó.
          </Callout>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {i18n.t("contactModal.buttons.cancel")}
        </Button>

        <div className={classes.btnWrapper}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={loading || !isFormValid()}
          >
            {labels.btn}
          </Button>
          {loading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderInputModal;